export enum TelemetryPage {
  Dashboard = 'dashboard',
  Session = 'session',
  Lap = 'lap',
  SectorAnalysis = 'sector',
  TyreAnalysis = 'tyre',
  TyreOverview = 'tyreOverview',
  LapDiffAnalysis = 'lapdiff',
  AggregationAnalysis = 'aggregation',
  AggregationDiffAnalysis = 'aggregationDiff',
  CornerAnalysis = 'corner',
  BrakingAnalysis = 'braking',
  Races = 'races',
  RaceSeason = 'raceSeason',
  RaceEvent = 'raceEvent',
  RaceEventCategory = 'raceEventCategory',

  RaceDrivers = 'raceDrivers',
  RaceDriver = 'raceDriver',
  RaceDriverPace = 'raceDriverPace',
  RaceDriverStats = 'raceDriverStats',

  Tracks = 'tracks',
  TrackDetail = 'trackDetail',
  Settings = 'settings',
  Inventory = 'inventory',
  Teams = 'teams',
  TeamDetail = 'teamDetail',
  Setup = 'setup',
  SetupCoach = 'setupCoach',
  AnalysisHistory = 'analysisHistory',
  Upload = 'upload',
  Login = 'login',
  OAuthAuthorize = 'oauthAuthorize',
  Privacy = 'privacy',
  Terms = 'terms',
}

interface ParsedRoute {
  page: TelemetryPage;
  params: Record<string, string>;
}

const ROUTE_PATTERNS: Array<{ pattern: RegExp; page: TelemetryPage; paramNames: string[] }> = [
  { pattern: /^#\/analysis\/history$/, page: TelemetryPage.AnalysisHistory, paramNames: [] },
  { pattern: /^#\/analysis\/corner(?:\/(.+))?$/, page: TelemetryPage.CornerAnalysis, paramNames: ['trackName'] },
  { pattern: /^#\/analysis\/braking(?:\/(.+))?$/, page: TelemetryPage.BrakingAnalysis, paramNames: ['trackName'] },
  { pattern: /^#\/analysis\/aggregation-diff(?:\/(.+))?$/, page: TelemetryPage.AggregationDiffAnalysis, paramNames: ['trackName'] },
  { pattern: /^#\/analysis\/aggregation(?:\/(.+))?$/, page: TelemetryPage.AggregationAnalysis, paramNames: ['trackName'] },
  { pattern: /^#\/analysis\/lapdiff(?:\/(.+))?$/, page: TelemetryPage.LapDiffAnalysis, paramNames: ['trackName'] },
  { pattern: /^#\/analysis\/tyre-overview$/, page: TelemetryPage.TyreOverview, paramNames: [] },
  { pattern: /^#\/analysis\/tyre(?:\/(.+))?$/, page: TelemetryPage.TyreAnalysis, paramNames: ['tyreId'] },
  { pattern: /^#\/analysis\/sector(?:\/(.+))?$/, page: TelemetryPage.SectorAnalysis, paramNames: ['trackName'] },
  { pattern: /^#\/analysis\/setup-coach$/, page: TelemetryPage.SetupCoach, paramNames: [] },
  { pattern: /^#\/session\/([^/]+)\/lap\/(\d+)(?:\/(speed|acceleration|rpm|gforce))?$/, page: TelemetryPage.Lap, paramNames: ['sessionId', 'lapNumber', 'tab'] },
  { pattern: /^#\/session\/([^/]+)$/, page: TelemetryPage.Session, paramNames: ['sessionId'] },

  { pattern: /^#\/tracks\/([^/]+)\/([^/]+)\/(layout|measured-laps|official-laps|races)$/, page: TelemetryPage.TrackDetail, paramNames: ['source', 'trackId', 'tab'] },
  { pattern: /^#\/tracks\/([^/]+)\/([^/]+)$/, page: TelemetryPage.TrackDetail, paramNames: ['source', 'trackId'] },
  { pattern: /^#\/tracks$/, page: TelemetryPage.Tracks, paramNames: [] },

  { pattern: /^#\/drivers$/, page: TelemetryPage.RaceDrivers, paramNames: [] },
  { pattern: /^#\/races\/driver\/([^/]+)\/stats$/, page: TelemetryPage.RaceDriverStats, paramNames: ['driverId'] },
  { pattern: /^#\/races\/driver\/([^/]+)\/pace$/, page: TelemetryPage.RaceDriverPace, paramNames: ['driverId'] },
  { pattern: /^#\/races\/driver\/([^/]+)\/races$/, page: TelemetryPage.RaceDriver, paramNames: ['driverId'] },
  { pattern: /^#\/races\/driver\/([^/]+)$/, page: TelemetryPage.RaceDriver, paramNames: ['driverId'] },
  { pattern: /^#\/races\/([^/]+)\/([^/]+)\/([^/]+)$/, page: TelemetryPage.RaceEventCategory, paramNames: ['championshipId', 'eventId', 'categoryId'] },
  { pattern: /^#\/races\/([^/]+)\/([^/]+)$/, page: TelemetryPage.RaceEvent, paramNames: ['championshipId', 'eventId'] },
  { pattern: /^#\/races\/([^/]+)$/, page: TelemetryPage.RaceSeason, paramNames: ['championshipId'] },
  { pattern: /^#\/races$/, page: TelemetryPage.Races, paramNames: [] },
  { pattern: /^#\/settings$/, page: TelemetryPage.Settings, paramNames: [] },
  { pattern: /^#\/inventory\/([^/]+)\/([a-z-]+)$/, page: TelemetryPage.Inventory, paramNames: ['teamId', 'subtab'] },
  { pattern: /^#\/inventory\/(engines|tyres|chassis|carburetors|axles|hubs|spark-plugs)$/, page: TelemetryPage.Inventory, paramNames: ['subtab'] },
  { pattern: /^#\/inventory\/([^/]+)$/, page: TelemetryPage.Inventory, paramNames: ['teamId'] },
  { pattern: /^#\/inventory$/, page: TelemetryPage.Inventory, paramNames: [] },
  { pattern: /^#\/teams\/([^/]+)\/(members|sharing)$/, page: TelemetryPage.TeamDetail, paramNames: ['teamId', 'tab'] },
  { pattern: /^#\/teams\/([^/]+)$/, page: TelemetryPage.TeamDetail, paramNames: ['teamId'] },
  { pattern: /^#\/teams$/, page: TelemetryPage.Teams, paramNames: [] },
  { pattern: /^#\/setup$/, page: TelemetryPage.Setup, paramNames: [] },
  { pattern: /^#\/tools\/setup-coach$/, page: TelemetryPage.SetupCoach, paramNames: [] },
  { pattern: /^#\/(?:upload|import)$/, page: TelemetryPage.Upload, paramNames: [] },
  { pattern: /^#\/login$/, page: TelemetryPage.Login, paramNames: [] },
  { pattern: /^#\/oauth\/authorize$/, page: TelemetryPage.OAuthAuthorize, paramNames: [] },
  { pattern: /^#\/privacy(?:-policy)?$/, page: TelemetryPage.Privacy, paramNames: [] },
  { pattern: /^#\/terms(?:-and-conditions)?$/, page: TelemetryPage.Terms, paramNames: [] },
];

export function parsePath(hash: string): ParsedRoute {
  // Split hash path from query string: #/path?key=val&key2=val2
  const qIdx = hash.indexOf('?');
  const hashPath = qIdx >= 0 ? hash.slice(0, qIdx) : hash;
  const queryString = qIdx >= 0 ? hash.slice(qIdx + 1) : '';

  // Parse query params using URLSearchParams which decodes both %XX
  // sequences and treats '+' as space (application/x-www-form-urlencoded).
  // Callers storing binary/base64 values MUST use encodeURIComponent() when
  // writing, so that literal '+' characters are escaped as %2B.
  const queryParams: Record<string, string> = {};
  if (queryString) {
    const sp = new URLSearchParams(queryString);
    sp.forEach((v, k) => { queryParams[k] = v; });
  }

  for (const route of ROUTE_PATTERNS) {
    const match = hashPath.match(route.pattern);
    if (match) {
      const params: Record<string, string> = { ...queryParams };
      route.paramNames.forEach((name, i) => { params[name] = match[i + 1]; });
      return { page: route.page, params };
    }
  }
  return { page: TelemetryPage.Dashboard, params: queryParams };
}
